import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";

import {
  makeSelectPayByTextForAccount,
  selectPayByTextByAccount,
  selectPayByTextMeta,
} from "store/paybytext/paybytext-selectors";
import {
  fetchPayByText,
  savePayByText,
} from "store/paybytext/paybytext-action";
import { PayByTextData } from "store/paybytext/paybytext-state";
import { useRequestDispatch } from "store/requestHooks";
import { scopes } from "constants/idp-scopes";
import { paymentAmountType } from "constants/common";
import { useInput } from "hooks";
import { getDefaultInputValue } from "utility";
import { useRootSelector } from "store";
import { selectAllPaymentMethods } from "store/paymentMethods/paymentMethods-slice";

export const usePayByTextForm = (accountInformationId?: number) => {
  const requestDispatch = useRequestDispatch(scopes.paymentCenter.payByText);

  const payByTextData = useSelector(
    useMemo(
      () => makeSelectPayByTextForAccount(accountInformationId),
      [accountInformationId]
    )
  );
  const payByTextMeta = useSelector(selectPayByTextMeta);
  const payByTextAccounts = useSelector(selectPayByTextByAccount);
  const paymentMethods = useRootSelector(selectAllPaymentMethods);

  const phoneNumber = useInput(
    getDefaultInputValue(payByTextData?.phoneNumber, "")
  );

  const initialPaymentAmountType =
    payByTextData?.paymentAmount === 0 || !payByTextData?.paymentAmount
      ? paymentAmountType.due_amount
      : paymentAmountType.other;

  const paymentAmountTypeInput = useInput(
    getDefaultInputValue(initialPaymentAmountType, paymentAmountType.due_amount)
  );

  const paymentAmount = useInput(
    getDefaultInputValue(payByTextData?.paymentAmount, "")
  );
  const paymentMethodId = useInput(
    getDefaultInputValue(
      payByTextData?.paymentMethodId ?? paymentMethods?.[0]?.id ?? "",
      paymentMethods?.[0]?.id ?? ""
    )
  );
  const paymentMethodSelection = useInput(
    getDefaultInputValue("saved_method", "saved_method")
  );

  const isEnabled = payByTextData?.status === 1 || payByTextData?.status === 2;

  useEffect(() => {
    phoneNumber.setValue(payByTextData?.phoneNumber ?? "");
    paymentAmountTypeInput.setValue(
      payByTextData?.paymentAmount === 0 || !payByTextData?.paymentAmount
        ? paymentAmountType.due_amount
        : paymentAmountType.other
    );
    paymentAmount.setValue(payByTextData?.paymentAmount ?? "");
    if (paymentMethods && paymentMethods.length > 0) {
      paymentMethodId.setValue(
        payByTextData?.paymentMethodId ?? paymentMethods[0].id
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payByTextData, paymentMethods]);

  const initialize = useCallback(async () => {
    if (accountInformationId == null) {
      return;
    }
    await requestDispatch(fetchPayByText, accountInformationId);
  }, [requestDispatch, accountInformationId]);

  const save = useCallback(
    async (override?: Partial<PayByTextData>) => {
      let statusToSave = override?.status ?? payByTextData?.status ?? 0;

      // If no status is provided in override, derive it from the current state
      if (override?.status == null) {
        if (isEnabled) {
          statusToSave = payByTextData?.status === 2 ? 2 : 1; // Keep 2 if pending, otherwise set to 1
        } else {
          statusToSave = 0;
        }
      }

      const paymentAmountToSave =
        Number(paymentAmountTypeInput.value) === paymentAmountType.other
          ? Number(paymentAmount.value) || 0
          : 0;

      const dataToSave: PayByTextData = {
        id: payByTextData?.id,
        conversationEnrollmentId: payByTextData?.conversationEnrollmentId,
        status: statusToSave,
        phoneNumber: phoneNumber.value,
        accountInformationId: accountInformationId ?? 0,
        paymentMethodId:
          override?.paymentMethodId ?? Number(paymentMethodId.value),
        paymentAmountType: Number(paymentAmountTypeInput.value),
        paymentAmount: paymentAmountToSave,
      };

      await requestDispatch(savePayByText, dataToSave).unwrap();
    },
    [
      requestDispatch,
      isEnabled,
      phoneNumber.value,
      paymentAmountTypeInput.value,
      paymentAmount.value,
      paymentMethodId.value,
      accountInformationId,
      payByTextData,
    ]
  );

  return {
    phoneNumber,
    paymentAmountTypeInput,
    paymentAmount,
    paymentMethodId,
    paymentMethodSelection,
    paymentMethods,
    isEnabled,
    save,
    initialize,
    meta: payByTextMeta,
    payByTextAccounts,
  };
};
